// ============================================================================
// AGENT HOOKS
// ============================================================================
// React hooks for Agent API operations

import { useState, useEffect, useCallback } from 'react';
import { agentService } from 'api/services/agentService';
import type {
  Agent,
  CreateAgentRequest,
  UpdateAgentRequest,
  AgentSearchRequest,
  ID,
} from 'api/types';

/**
 * Hook to fetch all agents (paginated)
 */
export const useAgents = (params?: AgentSearchRequest) => {
  const [data, setData] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Serialize params to avoid infinite re-render when caller passes an object literal.
  const paramsKey = JSON.stringify(params);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await agentService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchAgents,
  };
};

/**
 * Hook to fetch a single agent by ID
 */
export const useAgent = (id: ID) => {
  const [data, setData] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAgent = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await agentService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);

  return {
    data,
    loading,
    error,
    refetch: fetchAgent,
  };
};

/**
 * Hook to create a new agent
 */
export const useCreateAgent = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createAgent = async (
    data: CreateAgentRequest,
    options?: {
      onSuccess?: (agent: Agent) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await agentService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createAgent,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update an agent
 */
export const useUpdateAgent = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateAgent = async (
    id: ID,
    data: UpdateAgentRequest,
    options?: {
      onSuccess?: (agent: Agent) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await agentService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateAgent,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete an agent
 */
export const useDeleteAgent = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteAgent = async (
    id: ID,
    rowVersion: number,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await agentService.delete(id, rowVersion);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteAgent,
    loading,
    error,
    success,
  };
};
